import {
  getCheckoutBehaviorAttribution,
  getAcquisitionContext,
  getGoogleAnalyticsAttribution,
  trackEvent,
  trackMetaLead,
  trackRedditLead,
} from './analytics';
import { getPromoteKitCheckoutAttribution } from './promotekit';
import { originalAcquisition } from './signupAttribution';
import { rememberAuthAttempt, clearAuthAttempt } from './authActivity';

const STORAGE_KEY = 'instanttranscriber_auth_attribution';
const CHECKOUT_STORAGE_KEY = 'instanttranscriber_checkout_attribution';
const SIGNUP_ATTEMPT_STORAGE_KEY = 'instanttranscriber_signup_attempt';
const ATTRIBUTION_TTL_MS = 90 * 24 * 60 * 60 * 1000;
const SIGNUP_ATTEMPT_TTL_MS = 60 * 60 * 1000;
const MAX_ATTRIBUTION_STRING_LENGTH = 120;
const LANDING_HOSTS = new Set([
  'instanttranscriber.com',
  'www.instanttranscriber.com',
  'instanttranscriber-landing-main.pages.dev',
]);
const INTERNAL_REFERRER_HOSTS = new Set([
  ...LANDING_HOSTS,
  'app.instanttranscriber.com',
  'accounts.google.com',
  'checkout.stripe.com',
]);

const CHECKOUT_ATTRIBUTION_KEYS = [
  'acquisition_source',
  'acquisition_medium',
  'acquisition_campaign',
  'acquisition_referrer',
  'auth_source',
  'from_landing',
  'landing_path',
  'first_touch_source',
  'landing_locale',
  'landing_cta_id',
  'landing_cta_location',
  'landing_plan_intent',
  'signup_auth_method',
  'signup_auth_target',
  'landing_referrer_host',
  'landing_referrer_path',
  'checkout_source',
  'source',
  'source_intent',
  'intent',
  'cta_location',
  'checkout_cta_location',
  'plan',
  'plan_intent',
  'billing_interval',
  'offer_variant',
  'last_app_event_before_checkout',
  'last_limit_event',
  'last_uploaded_record_id',
  'promotekit_referral',
  'promotekit_via',
];

const PROFILE_ATTRIBUTION_KEYS = [
  'first_acquisition_source',
  'first_acquisition_medium',
  'first_acquisition_campaign',
  'first_acquisition_referrer',
  'first_source_intent',
  'ga_client_id',
  'ga_tracking_status',
  'signup_source',
  'first_used_app',
  'first_touch_source',
  'first_auth_source',
  'first_auth_method',
  'first_auth_target',
  'first_landing_path',
  'first_landing_locale',
  'first_landing_cta_id',
  'first_landing_cta_location',
  'first_landing_plan_intent',
  'first_landing_referrer_host',
  'first_landing_referrer_path',
];

const isBrowser = () => typeof window !== 'undefined';

const PERSISTED_FIRST_TOUCH_KEYS = [
  'auth_source',
  'from_landing',
  'landing_path',
  'first_touch_source',
  'landing_locale',
  'cta_id',
  'cta_location',
  'plan_intent',
  'billing_interval',
  'auth_method',
  'source',
  'source_intent',
  'intent',
  'referrer_host',
  'referrer_path',
];

const safeUrl = (value) => {
  if (!value) return null;
  try {
    return new URL(value);
  } catch {
    return null;
  }
};

const getReferrer = () => {
  if (!isBrowser()) return null;
  return safeUrl(document.referrer);
};

const getPersistedFirstTouchAttribution = () => {
  if (!isBrowser()) return {};
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    if (!stored || typeof stored !== 'object') return {};
    if (stored.updated_at && Date.now() - Number(stored.updated_at) > ATTRIBUTION_TTL_MS) {
      localStorage.removeItem(STORAGE_KEY);
      return {};
    }
    return stored;
  } catch {
    return {};
  }
};

const getStoredAttribution = () => {
  if (!isBrowser()) return {};
  try {
    const session = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}');
    return { ...session, ...getPersistedFirstTouchAttribution() };
  } catch {
    return getPersistedFirstTouchAttribution();
  }
};

const persistFirstTouchAttribution = (attribution = {}) => {
  if (!isBrowser()) return;
  const stored = getPersistedFirstTouchAttribution();
  const next = { ...stored };

  PERSISTED_FIRST_TOUCH_KEYS.forEach((key) => {
    const hasStoredValue = stored[key] !== null && stored[key] !== undefined && stored[key] !== '';
    const value = attribution[key];
    if (!hasStoredValue && value !== null && value !== undefined && value !== '') {
      next[key] = value;
    }
  });

  if (!PERSISTED_FIRST_TOUCH_KEYS.some((key) => next[key] !== undefined)) return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      ...next,
      updated_at: stored.updated_at || Date.now(),
    }));
  } catch {}
};

const setStoredAttribution = (attribution) => {
  if (!isBrowser()) return;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(attribution));
  } catch {}
  persistFirstTouchAttribution(attribution);
};

const hasAttributionSignal = (attribution = {}) => Boolean(
  attribution.auth_source
  || attribution.from_landing
  || attribution.landing_path
  || attribution.first_touch_source
  || attribution.cta_id
  || attribution.cta_location
  || attribution.plan_intent
  || attribution.billing_interval
  || attribution.auth_method
  || attribution.auth_target
  || attribution.source_intent
  || attribution.intent
);

const cleanAttributionValue = (value) => {
  if (value == null) return undefined;
  if (typeof value === 'boolean' || typeof value === 'number') return value;
  return String(value).slice(0, MAX_ATTRIBUTION_STRING_LENGTH);
};

const cleanAttributionParams = (params = {}, allowedKeys = CHECKOUT_ATTRIBUTION_KEYS) => {
  const cleaned = {};
  allowedKeys.forEach((key) => {
    const value = cleanAttributionValue(params[key]);
    if (value !== undefined) cleaned[key] = value;
  });
  return cleaned;
};

const getStoredCheckoutAttribution = () => {
  if (!isBrowser()) return {};
  try {
    return JSON.parse(sessionStorage.getItem(CHECKOUT_STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
};

const setStoredCheckoutAttribution = (attribution) => {
  if (!isBrowser()) return;
  try {
    sessionStorage.setItem(CHECKOUT_STORAGE_KEY, JSON.stringify(attribution));
  } catch {}
};

const firstTouchFromParams = (params) => {
  const acquisition = getAcquisitionContext();
  if (acquisition.acquisition_source) return `${acquisition.acquisition_source}/${acquisition.acquisition_medium || '(not set)'}`;
  const source = params.get('utm_source') || params.get('source');
  const medium = params.get('utm_medium');
  const campaign = params.get('utm_campaign');
  const sourceIntent = params.get('source_intent');
  if (source && medium) return `${source}/${medium}`;
  if (source) return source;
  if (sourceIntent) return `intent:${sourceIntent}`;
  if (campaign) return `campaign:${campaign}`;
  return undefined;
};

const deriveFirstTouchSource = ({
  params,
  referrer,
  stored,
  source,
  fromLanding,
  landingPath,
}) => {
  if (stored.first_touch_source) return stored.first_touch_source;

  const paramSource = firstTouchFromParams(params);
  if (paramSource) return paramSource;

  if (fromLanding) {
    return landingPath ? `landing:${landingPath}` : 'landing';
  }

  const referrerHost = referrer?.hostname;
  if (referrerHost && !INTERNAL_REFERRER_HOSTS.has(referrerHost)) {
    return `referrer:${referrerHost}`;
  }

  return source || undefined;
};

export const getAuthAttribution = () => {
  if (!isBrowser()) return {};

  const params = new URLSearchParams(window.location.search);
  const referrer = getReferrer();
  const stored = getStoredAttribution();
  const authSource = params.get('from') || stored.auth_source || '';
  const source = params.get('source') || stored.source || undefined;
  const currentReferrerHost = referrer?.hostname || '';
  const referrerHost = stored.referrer_host || currentReferrerHost;
  const isLandingReferrer = LANDING_HOSTS.has(currentReferrerHost) || LANDING_HOSTS.has(stored.referrer_host);
  const fromLanding = authSource === 'landing' || isLandingReferrer || stored.from_landing;
  const acquisition = getAcquisitionContext();
  const landingPath = acquisition.landing_path || params.get('landing_path') || stored.landing_path || undefined;
  const firstTouchSource = deriveFirstTouchSource({
    params,
    referrer,
    stored,
    source: source || authSource,
    fromLanding,
    landingPath,
  });

  return {
    ...acquisition,
    auth_source: fromLanding ? 'landing' : (authSource || undefined),
    from_landing: Boolean(fromLanding),
    landing_path: landingPath,
    first_touch_source: firstTouchSource,
    landing_locale: params.get('landing_locale') || stored.landing_locale || undefined,
    cta_id: params.get('cta_id') || stored.cta_id || undefined,
    cta_location: params.get('cta_location') || stored.cta_location || undefined,
    plan_intent: params.get('plan_intent') || stored.plan_intent || undefined,
    billing_interval: params.get('billing_interval') || stored.billing_interval || undefined,
    auth_method: params.get('auth_method') || stored.auth_method || undefined,
    auth_target: params.get('auth_target') || stored.auth_target || undefined,
    source,
    source_intent: params.get('source_intent') || stored.source_intent || acquisition.source_intent || undefined,
    intent: params.get('intent') || stored.intent || undefined,
    referrer_host: referrerHost || undefined,
    referrer_path: stored.referrer_path || referrer?.pathname || undefined,
  };
};

export const getCheckoutAuthAttribution = () => {
  const attribution = getAuthAttribution();
  return cleanAttributionParams({
    ...getAcquisitionContext(),
    auth_source: attribution.auth_source,
    from_landing: attribution.from_landing,
    landing_path: attribution.landing_path,
    first_touch_source: attribution.first_touch_source,
    source: attribution.source,
    source_intent: attribution.source_intent,
    intent: attribution.intent,
    landing_locale: attribution.landing_locale,
    landing_cta_id: attribution.cta_id,
    landing_cta_location: attribution.cta_location,
    landing_plan_intent: attribution.plan_intent,
    billing_interval: attribution.billing_interval,
    signup_auth_method: attribution.auth_method,
    signup_auth_target: attribution.auth_target,
    landing_referrer_host: attribution.referrer_host,
    landing_referrer_path: attribution.referrer_path,
  });
};

export const buildCheckoutAttribution = (params = {}, user) => {
  const attribution = cleanAttributionParams({
    ...getCheckoutAuthAttribution(),
    ...getCheckoutBehaviorAttribution(),
    ...params,
    ...originalAcquisition(user),
    checkout_cta_location: params.checkout_cta_location || params.cta_location,
  });
  // Old browser state and caller-supplied attribution must not reassign an
  // existing customer's subsequent checkout to a newly visited affiliate.
  delete attribution.promotekit_referral;
  delete attribution.promotekit_via;
  return { ...attribution, ...getPromoteKitCheckoutAttribution(user) };
};

export const storeCheckoutAttribution = (params = {}, user) => {
  const attribution = buildCheckoutAttribution(params, user);
  setStoredCheckoutAttribution(attribution);
  return attribution;
};

export const getSavedCheckoutAttribution = () => cleanAttributionParams(getStoredCheckoutAttribution());

export const clearCheckoutAttribution = () => {
  if (!isBrowser()) return;
  try {
    sessionStorage.removeItem(CHECKOUT_STORAGE_KEY);
  } catch {}
};

const setStoredSignupAttempt = (params = {}) => {
  if (!isBrowser()) return;
  const signupAttempt = {
    ...cleanAttributionParams(params, [
      'method',
      'source',
      'auth_source',
      'from_landing',
      'landing_cta_location',
      'landing_plan_intent',
      'billing_interval',
      'source_intent',
      'intent',
    ]),
    created_at: Date.now(),
  };
  try {
    sessionStorage.setItem(SIGNUP_ATTEMPT_STORAGE_KEY, JSON.stringify(signupAttempt));
  } catch {}
  try {
    localStorage.setItem(SIGNUP_ATTEMPT_STORAGE_KEY, JSON.stringify(signupAttempt));
  } catch {}
};

export const getSavedSignupAttempt = () => {
  if (!isBrowser()) return {};
  try {
    const stored = JSON.parse(
      sessionStorage.getItem(SIGNUP_ATTEMPT_STORAGE_KEY)
      || localStorage.getItem(SIGNUP_ATTEMPT_STORAGE_KEY)
      || '{}',
    );
    if (stored.created_at && Date.now() - Number(stored.created_at) > SIGNUP_ATTEMPT_TTL_MS) {
      sessionStorage.removeItem(SIGNUP_ATTEMPT_STORAGE_KEY);
      localStorage.removeItem(SIGNUP_ATTEMPT_STORAGE_KEY);
      return {};
    }
    return stored;
  } catch {
    return {};
  }
};

export const persistAuthAttribution = (authTarget) => {
  const attribution = getAuthAttribution();
  const nextAttribution = {
    ...getStoredAttribution(),
    ...attribution,
    auth_target: authTarget || attribution.auth_target,
  };

  if (hasAttributionSignal(nextAttribution)) {
    setStoredAttribution(nextAttribution);
  }

  return nextAttribution;
};

export const buildProfileAttribution = ({ method, authTarget, source, user } = {}) => {
  const attribution = { ...persistAuthAttribution(authTarget), ...originalAcquisition(user) };
  const signupAttempt = getSavedSignupAttempt();
  const firstAuthMethod = method || signupAttempt.method || attribution.auth_method;
  const firstAuthTarget = authTarget || attribution.auth_target;

  return cleanAttributionParams({
    signup_source: source || 'webapp',
    first_acquisition_source: attribution.acquisition_source,
    first_acquisition_medium: attribution.acquisition_medium,
    first_acquisition_campaign: attribution.acquisition_campaign,
    first_acquisition_referrer: attribution.acquisition_referrer,
    first_source_intent: attribution.source_intent,
    ...getGoogleAnalyticsAttribution(),
    first_used_app: 'webapp',
    first_touch_source: attribution.first_touch_source,
    first_auth_source: attribution.auth_source || signupAttempt.auth_source || (attribution.from_landing ? 'landing' : undefined),
    first_auth_method: firstAuthMethod,
    first_auth_target: firstAuthTarget,
    first_landing_path: attribution.landing_path,
    first_landing_locale: attribution.landing_locale,
    first_landing_cta_id: attribution.cta_id,
    first_landing_cta_location: attribution.cta_location || signupAttempt.landing_cta_location,
    first_landing_plan_intent: attribution.plan_intent || signupAttempt.landing_plan_intent,
    first_landing_referrer_host: attribution.referrer_host,
    first_landing_referrer_path: attribution.referrer_path,
  }, PROFILE_ATTRIBUTION_KEYS);
};

export const clearSignupAttempt = () => {
  if (!isBrowser()) return;
  try {
    sessionStorage.removeItem(SIGNUP_ATTEMPT_STORAGE_KEY);
  } catch {}
  try {
    localStorage.removeItem(SIGNUP_ATTEMPT_STORAGE_KEY);
  } catch {}
};

export const captureAuthAttribution = (authTarget) => {
  return persistAuthAttribution(authTarget);
};

export const clearCompletedAuthContext = () => {
  if (!isBrowser()) return;
  // Keep the original landing/CTA for checkout attribution. The target of a
  // finished login/signup is temporary and must not describe later activity.
  try {
    const stored = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}');
    if (stored && typeof stored === 'object') {
      delete stored.auth_target;
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
    }
    const url = new URL(window.location.href);
    if (url.searchParams.has('auth_target')) {
      url.searchParams.delete('auth_target');
      window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
    }
  } catch {}
};

export const trackAuthPageView = (authTarget) => {
  const attribution = captureAuthAttribution(authTarget);
  const params = { auth_target: authTarget, ...attribution };

  trackEvent('auth_page_view', params);
  trackEvent(`${authTarget}_page_view`, attribution);

  if (attribution.from_landing) {
    trackEvent('auth_landing_arrival', params);
    trackEvent(`${authTarget}_landing_arrival`, attribution);
  }
};

export const trackAuthEvent = (eventName, params = {}) => {
  if (eventName === 'login_attempt' || eventName === 'signup_attempt') {
    rememberAuthAttempt(params.method, eventName === 'signup_attempt' ? 'signup' : 'login');
  }
  if (eventName === 'login_error' || eventName === 'signup_error') clearAuthAttempt();
  const attribution = getAuthAttribution();
  const target = eventName.startsWith('login_') ? 'login' : eventName.startsWith('signup_') ? 'signup' : undefined;
  const nextParams = { ...attribution, ...(target ? { auth_target: target } : {}), ...params };

  if (eventName === 'signup_email_form_open' || eventName === 'signup_attempt') {
    const method = params.method || (eventName === 'signup_email_form_open' ? 'email' : undefined);
    const leadParams = {
      method,
      source: 'signup_page',
      auth_source: attribution.auth_source,
      from_landing: attribution.from_landing,
      landing_cta_location: attribution.cta_location,
      landing_plan_intent: attribution.plan_intent,
      billing_interval: attribution.billing_interval,
      source_intent: attribution.source_intent,
      intent: attribution.intent,
    };
    setStoredSignupAttempt(leadParams);
    trackMetaLead(leadParams);
    trackRedditLead();
  }

  trackEvent(eventName, nextParams);
  if (attribution.from_landing) {
    trackEvent(`${eventName}_from_landing`, nextParams);
  }
};
